import React from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Form, InputField } from "@mimber/forms"
import { Button, Space, message } from "@mimber/ui-web"
import { useContactSupport } from "@mimber/client/mimber-organization"
import { extractApiErrorMessage } from "@mimber/client"
import {
  ContactSupportFormSchema,
  type ContactSupportFormData,
} from "./ContactSupportForm.schema"
import type { ContactSupportFormProps } from "./ContactSupportForm.types"

export const ContactSupportForm: React.FunctionComponent<
  ContactSupportFormProps
> = ({ onSuccess, onCancel }) => {
  const form = useForm<ContactSupportFormData>({
    resolver: zodResolver(ContactSupportFormSchema),
    mode: "onBlur",
    defaultValues: {
      subject: "",
      message: "",
    },
  })

  const contactSupport = useContactSupport({
    onSuccess: () => {
      message.success("Your message has been sent to our support team.")
      onSuccess?.()
    },
    onError: (error) => {
      message.error(
        extractApiErrorMessage(error, "Something went wrong. Please try again.")
      )
    },
  })

  const handleSubmit = (data: ContactSupportFormData) => {
    const currentUrl = new URL(window.location.href)
    contactSupport.mutate({
      ...data,
      pageUrl: `${currentUrl.origin}${currentUrl.pathname}`,
    })
  }

  return (
    <Form form={form} onSubmit={handleSubmit}>
      <InputField
        name="subject"
        type="text"
        label="Subject"
        placeholder="What do you need help with?"
        required
      />
      <InputField
        name="message"
        multiline
        rows={5}
        label="Message"
        placeholder="Describe your issue or question in detail"
        required
      />
      <Space style={{ justifyContent: "flex-end", width: "100%" }}>
        <Button onClick={onCancel} disabled={contactSupport.isPending}>
          Cancel
        </Button>
        <Button
          type="primary"
          htmlType="submit"
          loading={contactSupport.isPending}
        >
          Send Message
        </Button>
      </Space>
    </Form>
  )
}
